import { useState } from 'react';
import {
  Button,
  Input,
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from '@nextui-org/react';
import { toast } from 'react-toastify';
import { ExporterReceiver } from 'common/src/utils/types';
import useReceiverStore from '../../store/useReceiverStore';

interface AddClientModalProps {
  isOpen: boolean;
  onClose: () => void;
  exporterId: number;
  onClientAdded?: (receiver: ExporterReceiver) => void;
  existingClientNames?: string[];
  onClientAddedName?: (name: string) => void;
}

export const AddClientModal = ({
  isOpen,
  onClose,
  exporterId,
  onClientAdded,
  existingClientNames = [],
  onClientAddedName,
}: AddClientModalProps) => {
  const [newClientName, setNewClientName] = useState('');
  const [loading, setLoading] = useState(false);
  const addExporterReceiver = useReceiverStore(
    (state) => state.addExporterReceiver
  );

  const handleAdd = async () => {
    if (!newClientName.trim()) {
      toast.warn('El nombre del cliente no puede estar vacío.');
      return;
    }
    // Prevent duplicates by name (case and whitespace insensitive)
    const normalizedNew = newClientName.trim().toUpperCase();
    const hasDuplicate = existingClientNames.some(
      (n) => n?.toString().trim().toUpperCase() === normalizedNew
    );
    if (hasDuplicate) {
      toast.warn('Ya existe un cliente con ese nombre.');
      return;
    }
    setLoading(true);
    try {
      const createdReceiver = await addExporterReceiver(
        exporterId,
        normalizedNew
      );
      toast.success('Cliente añadido con éxito.');
      setNewClientName('');
      onClientAdded?.(createdReceiver);
      onClientAddedName?.(createdReceiver.name);
      onClose();
    } catch (error) {
      toast.error('Error al añadir el cliente.');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setNewClientName('');
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose}>
      <ModalContent>
        {() => (
          <>
            <ModalHeader>Añadir Nuevo Cliente</ModalHeader>
            <ModalBody>
              <Input
                autoFocus
                label='Nombre del cliente'
                placeholder='Ingrese el nombre del cliente'
                value={newClientName}
                onChange={(e) => setNewClientName(e.target.value.toUpperCase())}
                onKeyDown={(e) => e.key === 'Enter' && handleAdd()}
              />
            </ModalBody>
            <ModalFooter>
              <Button variant='light' onPress={handleClose}>
                Cancelar
              </Button>
              <Button color='primary' onPress={handleAdd} isLoading={loading}>
                Añadir
              </Button>
            </ModalFooter>
          </>
        )}
      </ModalContent>
    </Modal>
  );
};
